import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import {
  AppFeatures,
  AuthService,
  Editor,
  EditorForResource,
  FeatureSubjects,
  JSONRPCError,
  JSONRPCErrorCode,
  NodeType,
  Path,
  Permission,
  PermissionLookup,
  PermissionSubject,
  PermissionVerb,
  PermissionVerbs,
  Tree,
  TreeConfig,
  TreeNode,
  TreeResource,
  WebsocketClient,
  WebsocketService
} from '@s8l/client-tree-lib';
import { BehaviorSubject, of, Subscription, timer } from 'rxjs';
import { catchError, debounce, delay, filter, first, map, mergeMap, tap, timeout } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { CmsServerService, TreeFindReferencesResponse, TreePermissionRecursiveResponse } from './jsonrpc/cms-server.service';
import { UserServerService } from './jsonrpc/user-server.service';
import { UserService } from './user.service';

export function nodeHasAnyResouce(node: TreeNode) {
  // flashcards are not explizitly managed, so we exclude them from the count
  const count = node.resources.filter(r => r != TreeResource.Flashcard && r != TreeResource.Media && r != TreeResource.Description).length;
  return count > 0;
}

const WAIT_FOR_NODE_TIMEOUT_MS = 60000;

@Injectable({
  providedIn: 'root'
})
export class CmsService implements CanActivate {
  public languages = ['de', 'en', 'fr', 'it', 'es'];

  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
  private _language = environment.default_language;

  public rootNode: string;
  public appFeatures: AppFeatures[];
  public config: TreeConfig;
  public permissions: PermissionLookup;
  public subjects: PermissionSubject[];
  public colorPalette: { [name: string]: string } = {};

  public tree = new BehaviorSubject<Tree>(null);

  private generation = 0;
  private socket: WebsocketClient;
  private socketSubscription: Subscription;
  private nodeCreationInProgress = false;

  private _legacyPermissionSystem = false;

  public get language() {
    return this._language;
  }
  private set language(val) {
    this._language = val;
    Path.defaultLanguage = val;
  }

  public get legacyPermissionSystem(): boolean {
    return this._legacyPermissionSystem;
  }

  constructor(
    public cms: CmsServerService,
    private users: UserService,
    private user: UserServerService,
    private socketService: WebsocketService,
    private auth: AuthService,
    private router: Router
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | UrlTree {
    if (!this.auth.isAuth) {
      return this.router.createUrlTree(['/login', state.url]);
    }
    return this.init().then(async () => {
      try {
        // Extract path from URL: /node/{path}?language=...
        const urlPath = state.url;

        // Match pattern: /node/([^?]+)
        const match = urlPath.match(/\/node\/([^?]+)/);
        const nodePath = match ? decodeURIComponent(match[1]) : null;

        if (nodePath) {
          const pathSegments = Path.parse(nodePath).get();

          // If path has more than 2 segments (root + at least 2 children), load the path
          if (pathSegments.length > 2) {
            const node = await this.loadPathToNode(nodePath);

            // If the loaded node (or any ancestor) is a reference, load the original into the tree
            if (node?.type === NodeType.Reference) {
              await this.loadOriginalForReference(node);
            }
          }
        }
      } catch (error) {
        console.error('[Route Guard] Error during path loading:', error);
      }
      return true;
    });
  }

  public applyLanguage(val: string): Promise<Tree> {
    if (val == this.language) {
      return Promise.resolve(this.tree.value);
    }

    let ensureRoot = Promise.resolve();
    if (!this.tree.value.root.names[val]) {
      ensureRoot = this.cms.treeLink({
        reference: new Path([this.tree.value.root.name]).uri(this._language),
        path: new Path([this.tree.value.root.name]).uri(val)
      });
    }
    this.language = val;
    return ensureRoot
      .then(() =>
        this.cms.treeGet({
          language: val,
          depth: 1
        })
      )
      .then(tree => this.updateTree(tree));
  }

  public updateConfig() {
    this.appFeatures = environment.features as AppFeatures[];
    this.rootNode = environment.tree.root_node;
    this.config = new TreeConfig(environment.tree.modifiers, environment.tree.rules, environment.tree.templates);

    const root = document.documentElement;
    if (environment?.wiki?.colors) {
      for (const key of Object.keys(environment.wiki.colors)) {
        root.style.setProperty(`--custom-${key}`, environment.wiki.colors[key]);
      }
    }
    for (let i = 1; i < 11; i++) {
      const val = getComputedStyle(root).getPropertyValue(`--custom-color-${i}`);
      this.colorPalette[`color-${i}`] = val;
    }
  }

  public init(): Promise<any> {
    if (this.socket || this.socketSubscription) {
      return Promise.resolve();
    }

    this.updateConfig();

    return Promise.all([this.cms.treeGet({ language: this.language, depth: 1 }), this.loadKnownSubjects()])
      .then(data => {
        return this.updateTree(data[0]);
      })
      .then(data => {
        const sidemenu = data.root.children.find(c => c.name === 'Sidemenu');

        if (!sidemenu) {
          return;
        }

        return this.updateParentNodeChildren(sidemenu.uuid, Path.parse(sidemenu.path));
      })
      .then(() => {
        return this.users.init();
      })
      .then(() => this.socketService.connect(environment.UPDATE_WS))
      .then(socket => {
        this.socket = socket;

        this.socketSubscription = this.socket
          .onRequest()
          .pipe(
            tap(payload => void this.socket.respond(payload.id, {})),
            filter(
              payload => payload.method == 'update' && parseInt(payload.params, 10) != this.generation && !this.nodeCreationInProgress
            ),
            debounce(() => timer(500)),
            mergeMap(() =>
              this.cms.treeGet({
                language: this.language,
                depth: 1,
                full: false
              })
            ),
            catchError(err => {
              console.warn(err);
              return of({
                root: null,
                generation: this.generation
              });
            }),
            filter(tree => tree.root != null || tree.generation !== this.generation)
          )
          .subscribe(tree => void this.updateTree(tree));
      });
  }

  public deinit() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
      this.socketSubscription = null;
    }
  }

  public translate(path: Path, lang: string) {
    let result = new Path([], lang);

    let current = this.tree.value.root;
    for (const p of path.removeFirst().get()) {
      if (!current || !current.children) {
        return null;
      }
      result = result.addLast(current.names[lang] || current.name);
      current = current.children.find(c => c.name === p);
    }
    if (!current) {
      return null;
    }
    return result.addLast(current.names[lang] || current.name);
  }

  public hasFeatures(features: AppFeatures[]) {
    if (!this.appFeatures) {
      return false;
    }
    return features.every(v => this.appFeatures.includes(v));
  }

  public getAccess(subject: PermissionSubject, nodeUuid: string = null): PermissionVerb {
    const payload = this.auth.payload;
    if (!payload) {
      return 0 as PermissionVerb;
    }

    const generalVerb = this.permissions.getAccess(payload.roles, subject);
    if (generalVerb <= 0 || !nodeUuid) {
      return generalVerb;
    }

    const node = this.tree.value.getByUUID(nodeUuid);
    const nodeVerb = node?.permissions?.getAccess(payload.roles, subject) ?? 0;
    return generalVerb & nodeVerb;
  }

  public hasAccess(perm: Permission, nodeUuid: string = null): boolean {
    const verb = this.getAccess(perm.subject, nodeUuid);
    return (verb & perm.verbs) === perm.verbs;
  }

  public updateAccessVerb(role: string, add: boolean, node: TreeNode, perms: Permission[]): Promise<any> {
    // Local state: affected permission set to be updated
    // Update general permissions for node == null.
    const permissions = node ? node.permissions : this.permissions;
    // A reference node's own ACL is keyed by its own permissionUuid (its
    // reference id), not the target node's uuid - see cms-server's
    // Node#permissionUuid(). Not on the TreeNode type yet (comes from
    // @s8l/client-tree-lib), but cms-server now always sends it.
    const resource = (node as any)?.permissionUuid ?? node?.uuid;
    return this.pushAccessVerb(role, add, resource, perms).then(() =>
      perms.forEach(p => this.updateLocalVerb(permissions, role, add, p.subject, p.verbs))
    );
  }

  public pushAccessVerb(role: string, add: boolean, resource: string, perms: Permission[]): Promise<any> {
    if (this._legacyPermissionSystem) {
      const payload = {
        role,
        permission: perms.flatMap(p => this.legacyPermissions(p, resource))
      };
      return add ? this.user.permissionLegacyCreate(payload) : this.user.permissionLegacyDelete(payload);
    } else {
      const payload = { role, resource, permission: perms.map(p => p.key()) };
      return add ? this.user.permissionAdd(payload) : this.user.permissionRemove(payload);
    }
  }

  // Grants/revokes a permission on a node and its whole subtree in one
  // server-side call (see cms-server's /tree:update-permission-recursive).
  // Unlike updateAccessVerb/pushAccessVerb this never loops per-node
  // client-side - the tree is lazy-loaded so the client only ever knows
  // about whatever's currently expanded, which previously made "recursive"
  // silently mean "immediate children only".
  public async updatePermissionRecursive(
    role: string,
    add: boolean,
    node: TreeNode,
    perms: Permission[]
  ): Promise<TreePermissionRecursiveResponse> {
    const response = await this.cms.treeUpdatePermissionRecursive({
      path: node.path,
      role,
      add,
      permission: perms.map(p => p.key())
    });
    await this.refreshSubtreePermissions(node);
    return response;
  }

  // After a recursive permission change, the in-memory tree's `permissions`
  // data is stale for the target node and every descendant. Only refresh
  // nodes already loaded client-side (don't trigger loading of
  // collapsed/unloaded branches - that would change UI expand state
  // unexpectedly); re-emit the tree once at the end, not per-node, to avoid
  // a re-render storm on a large already-expanded subtree.
  public async refreshSubtreePermissions(node: TreeNode): Promise<void> {
    if (!node) {
      return;
    }
    await this.refreshLoadedPermissions(node);
    this.notifyTreeUpdated();
  }

  private async refreshLoadedPermissions(node: TreeNode): Promise<void> {
    try {
      // path, not parentUuid/uuid: a uuid alone can't identify a specific
      // reference node's own occurrence in the tree (it resolves to the
      // target's canonical path instead), so refreshing by uuid could
      // silently refresh a different occurrence than the one just edited.
      const response = await this.cms.treeGet({
        language: this.language,
        path: node.path,
        depth: 1
      });
      if (response.root?.permissions) {
        node.permissions = PermissionLookup.of(response.root.permissions);
      }
    } catch (error) {
      console.error(`[Permission Refresh] Failed to refresh permissions for ${node.path}:`, error);
    }

    if (!node.children?.length) {
      return;
    }
    await Promise.all(node.children.map(child => this.refreshLoadedPermissions(child)));
  }

  private legacyPermissions(permission: Permission, resource: string) {
    return PermissionVerbs.toLegacyStrings(permission.verbs).map(verb => this.legacySubjectKey(permission.subject, resource) + '::' + verb);
  }

  private legacySubjectKey(subject: PermissionSubject, node: string): string {
    let key = subject.base;
    if (subject.sub != null) {
      key += '#' + subject.sub;
    }
    if (node != null) {
      if (subject.sub == null) {
        key += '#'; // add marker for empty subclass
      }
      key += '#' + node;
    }
    return key;
  }

  private updateLocalVerb(lookup: PermissionLookup, role: string, add: boolean, subject: PermissionSubject, verb: PermissionVerb) {
    const key = subject.key();
    let verbs = lookup.grants[role];
    if (!verbs) {
      verbs = {};
      lookup.grants[role] = verbs;
    }
    if (add) verbs[key] = (verbs[key] ?? 0) | verb;
    else verbs[key] = (verbs[key] ?? 0) & ~verb;
  }

  public getNodeDescription(path: Path): Promise<string> {
    return this.cms
      .wikiGet({ path: path.uri() }, 'description')
      .then(res => {
        return res.fields.content.value;
      })
      .catch(err => {
        if (err.code === JSONRPCErrorCode.NOT_FOUND) {
          return '';
        }
      });
  }

  public findReferences(path: Path): Promise<TreeFindReferencesResponse> {
    return this.cms.treeFindReferences({ path: path.uri(this.language), language: this.language }).catch(err => {
      if (err.code === JSONRPCErrorCode.NOT_FOUND) {
        return null;
      } else {
        throw err;
      }
    });
  }

  public resolveReferenceRecursive(n: TreeNode): Path {
    if (n == null) {
      return null;
    }

    if (n.type == NodeType.Reference) {
      const ref = this.tree.value.getByUUID(n.uuid);
      if (ref == null) {
        return null;
      }
      return Path.parse(ref.path);
    }

    const path = Path.parse(n.path);
    if (path.length <= 1) {
      return null; // root
    }
    const parent = this.tree.value.get(path.removeLast());
    const parentRef = this.resolveReferenceRecursive(parent);
    if (parentRef == null) {
      return null;
    }

    return parentRef.addLast(path.getLast());
  }

  /**
   * Fetches the original node for a reference node and loads it into the tree.
   * Returns the resolved path to the original node, or null if it could not be loaded.
   */
  public async loadOriginalForReference(n: TreeNode): Promise<Path> {
    if (n == null || n.type !== NodeType.Reference) {
      return null;
    }

    // n.uuid is the original node's UUID (set by the server Lua script)
    try {
      const response = await this.cms.treeGet({
        language: this.language,
        parentUuid: n.uuid,
        depth: 1
      });

      if (response?.root?.path) {
        // Load the original node's full path into the tree
        await this.loadPathToNode(response.root.path);
        // Now the original should be in the tree, resolve again
        return this.resolveReferenceRecursive(n);
      }
    } catch (error) {
      console.error('[Reference] Failed to load original node for reference:', error);
    }

    return null;
  }

  public hasResourceForTranslation(path: Path, mode: string): Promise<boolean> {
    switch (mode) {
      case Editor.Casestudy:
      case Editor.Glossary:
      case Editor.Impuls:
      case Editor.Wiki:
        return this.cms
          .wikiGet({ path: path.uri(this.language) }, mode)
          .then(data => data?.fields?.content?.value != null)
          .catch(() => false);
      default:
        return Promise.resolve(true);
    }
  }

  public deleteResource(path: Path, resource: TreeResource) {
    switch (resource) {
      case TreeResource.Wiki:
      case TreeResource.Glossary:
      case TreeResource.Casestudy:
      case TreeResource.Impuls:
      case TreeResource.Video:
        return this.cms.wikiDelete(
          {
            path: path.uri(this.language)
          },
          EditorForResource[resource]
        );

      case TreeResource.Quiz:
        return this.cms.quizDelete({
          path: path.uri(this.language)
        });
    }
  }

  public waitForNode(path: Path, timeoutMillis: number = WAIT_FOR_NODE_TIMEOUT_MS): Promise<TreeNode> {
    return this.tree
      .pipe(
        debounce(() => timer(100)),
        map(tree => tree.get(path)),
        filter(node => !!node),
        timeout(timeoutMillis),
        first(),
        delay(100)
      )
      .toPromise();
  }

  public fmtPath(path: string | Path): string {
    if (!path || path.length <= 1) {
      return '';
    }
    const p = path instanceof Path ? path : Path.parse(path);
    return p.removeFirst().get().join(' / ');
  }

  // Returns error details (or null when allowed) when attempting
  // to move/clone/reference a subtree to another depth level.
  // Result is null for success or [message, resource, original path, new depth].
  public recursiveResourceReason(node: TreeNode, depth: number): [string, TreeResource, Path, number] {
    const rule = this.config.findRuleForLevel(depth, []);
    for (const r of node.resources) {
      if (r === TreeResource.Flashcard || r === TreeResource.Description) {
        continue;
      }
      if (!rule.resource.includes(r) && rule.reason) {
        const msg = rule.reason[r] || 'Invalid resource ' + r;
        const path = Path.parse(node.path);
        return [msg, r, path, depth];
      }
    }
    return node?.children.map(c => this.recursiveResourceReason(c, depth + 1)).find(r => r);
  }

  private mergeTree(prev: TreeNode, curr: TreeNode): TreeNode {
    // Merge children: include all from current, plus any from previous that aren't in current
    // This preserves lazy-loaded descendants when shallow trees are merged
    const mergedChildren = [
      // Merged children from current tree
      ...(curr.children?.map(c =>
        this.mergeTree(
          prev?.children.find(p => p.uuid == c.uuid),
          c
        )
      ) || []),
      // Preserve children from previous tree that aren't in current
      // (these are lazy-loaded nodes that should be kept)
      ...(prev?.children?.filter(p => !curr.children?.find(c => c.uuid === p.uuid)) || [])
    ];

    return new TreeNode({
      ...curr,
      names: curr.names || prev?.names,
      permissions: curr.permissions || prev?.permissions,
      children: mergedChildren
    });
  }

  private verifyTree(n: TreeNode) {
    if (!n.names || !n.permissions) {
      return false;
    }
    for (const c of n.children) {
      if (!this.verifyTree(c)) {
        return false;
      }
    }
    return true;
  }

  private updateTree(tree): Promise<Tree> {
    this.generation = tree.generation;

    if (tree.permissions) {
      this.permissions = PermissionLookup.of(tree.permissions);
    }

    const val = new Tree(this.mergeTree(this.tree.value?.root, tree.root));

    if (!this.verifyTree(val.root)) {
      return this.cms
        .treeGet({
          language: this.language,
          depth: 1
        })
        .then(t => this.updateTree(t));
    } else {
      this.tree.next(val);
      return Promise.resolve(val);
    }
  }

  public notifyTreeUpdated(): void {
    // Emit current tree to notify subscribers (e.g., when children are lazy-loaded)
    if (this.tree.value) {
      this.tree.next(this.tree.value);
    }
  }

  public setNodeCreationInProgress(inProgress: boolean): void {
    // Disable/enable socket updates during node creation
    // This prevents socket updates from interfering with the targeted parent update
    this.nodeCreationInProgress = inProgress;
  }

  public async loadPathToNode(nodePath: string): Promise<TreeNode | null> {
    // Load a specific deep node by sequentially fetching each level with depth: 1
    // E.g., for path "root/child/child2", fetch root, then child, then child2

    if (!this.tree.value) {
      return null;
    }

    const pathSegments = Path.parse(nodePath).get();
    if (pathSegments.length === 0) {
      return null;
    }

    let currentNode = this.tree.value.root;

    // Skip root, start from first child
    for (let i = 1; i < pathSegments.length; i++) {
      const segmentName = pathSegments[i];

      // Find child with matching name
      let child = currentNode.children?.find(c => c.names?.[this.language] === segmentName);

      if (!child) {
        try {
          const response = await this.cms.treeGet({
            language: this.language,
            parentUuid: currentNode.uuid,
            depth: 1
          });

          if (response.root?.children) {
            // Update current node's children - convert plain objects to TreeNode instances
            currentNode.children = (response.root.children || []).map(rawChild => this.createTreeNode(rawChild));
            child = currentNode.children.find(c => c.names?.[this.language] === segmentName);
          }
        } catch (error) {
          console.error(`[Path Loading] Failed to fetch children for ${currentNode.path}:`, error);
          return null;
        }
      }

      if (!child) {
        console.error(`[Path Loading] Could not find node segment: "${segmentName}"`);
        return null;
      }

      currentNode = child;
    }

    this.notifyTreeUpdated();
    return currentNode;
  }

  public async updateParentNodeChildren(parentUuid: string, parentPath: Path): Promise<TreeNode | null> {
    // Fetch and update a specific parent node's children
    // Used when creating a new child node to ensure the parent has the latest children loaded
    if (!this.tree.value) {
      return null;
    }

    try {
      const response = await this.cms.treeGet({
        language: this.language,
        parentUuid: parentUuid,
        depth: 1
      });

      if (response.root?.children) {
        // Find the parent node in the tree
        const parentNode = this.tree.value.getByUUID(parentUuid);
        if (parentNode) {
          // Update parent's children with fetched data, converting to TreeNode instances
          parentNode.children = (response.root.children || []).map(rawChild => this.createTreeNode(rawChild));

          // Emit updated tree to notify all subscribers
          this.notifyTreeUpdated();
          return parentNode;
        }
      }
    } catch (error) {
      console.error(`[Parent Update] Failed to fetch children for parent ${parentPath?.uri()}:`, error);
    }

    return null;
  }

  private createTreeNode(data: any): TreeNode {
    // Convert plain objects to actual TreeNode instances with all methods
    return new TreeNode({
      ...data,
      children: (data.children || []).map((child: any) => this.createTreeNode(child))
    });
  }

  private loadKnownSubjects(): Promise<void> {
    return this.user
      .permissionSubjects() // Load all known subjects from the server...
      .then(res => {
        this._legacyPermissionSystem = false;
        return res;
      })
      .catch(err => {
        if (err instanceof JSONRPCError && err.code === -32601) {
          // ... or fall back to RBAC version 2 and use some sane defaults...
          this._legacyPermissionSystem = true;
          return ['licence', 'node', 'role', 'stats', 'user'];
        } else {
          throw err;
        }
      })
      .then(keys => {
        // ... then add subjects that are required for features.
        const subjects = keys.map(key => PermissionSubject.parse(key));
        this.appFeatures
          .map(f => FeatureSubjects[f])
          .filter(s => s)
          .filter(s => !subjects.find(x => s.equals(x)))
          .forEach(s => {
            if (subjects.every(x => !x.equals(s))) subjects.push(s);
          });
        this.subjects = subjects.sort((a, b) => a.key().localeCompare(b.key()));
      });
  }
}
